import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * <bundle-pack>
 *
 * Self-contained Web Component that:
 *  - reads tier configuration and cart-add URL from data-* attributes
 *  - tracks per-variant quantities in an internal Map
 *  - drives the progress meter (filled circles, fill bar, shipping badge)
 *  - validates a minimum total quantity before enabling the submit button
 *  - POSTs all selected items in one call to /cart/add.js with {items: [...]}
 *  - dispatches CartAddEvent / CartErrorEvent so the cart drawer + count badge
 *    pick up the change without any extra wiring.
 *
 * @typedef {object} BundlePackRefs
 * @property {HTMLElement} progressLine
 * @property {HTMLElement} [meterBadge]
 *
 * @extends Component<BundlePackRefs>
 */
class BundlePack extends Component {
  requiredRefs = ['progressLine'];

  #abortController = new AbortController();
  /** @type {Map<string, { quantity: number, price: number, comparePrice: number, title: string, available: boolean }>} */
  #selected = new Map();
  /** @type {Array<{ qty: number, discountType: 'none' | 'percentage' | 'fixed', discount: number }>} */
  #tiers = [];
  #minQty = 0;
  /** 'euro' = unlock by cart total in cents, 'quantity' = unlock by total sachet count. */
  #freeShippingMode = 'euro';
  /** Free-shipping threshold in cents. 0 = never unlock the badge. */
  #freeShippingThreshold = 0;
  /** Free-shipping threshold in total quantity. 0 = never unlock the badge. */
  #freeShippingMinQty = 0;
  /** Subscribe-mode state, synced from <bundle-subscribe> via custom event. */
  #subscribeMode = 'one-time';
  /** Frequency value (string), only meaningful when subscribeMode === 'subscribe'. */
  #subscribeFrequency = /** @type {string | null} */ (null);
  /** Subscription discount (%), supplied by <bundle-subscribe> on its event. */
  #subscribeDiscountPct = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#parseConfig();
    this.#bindEvents();
    // Defer the first paint so circles have measurable offsetLeft when we
    // align the progress line on them.
    requestAnimationFrame(() => this.#updateUI());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #parseConfig() {
    try {
      /** @type {Array<{ qty: number | string, discountType?: string, discount: number | string }>} */
      const raw = JSON.parse(this.dataset.tiers || '[]');
      // discount comes through as a string from Liquid to preserve decimals
      // (Shopify number settings round to 1 decimal). Normalise to a real number here.
      this.#tiers = raw.map((tier) => ({
        qty: Number(tier.qty) || 0,
        discountType: /** @type {'none' | 'percentage' | 'fixed'} */ (tier.discountType || 'none'),
        discount: parseFloat(String(tier.discount)) || 0,
      }));
    } catch {
      this.#tiers = [];
    }
    this.#minQty = Number(this.dataset.minTotalQuantity || 0);
    this.#freeShippingMode = this.dataset.freeShippingMode === 'quantity' ? 'quantity' : 'euro';
    this.#freeShippingThreshold = Number(this.dataset.freeShippingThreshold || 0);
    this.#freeShippingMinQty = Number(this.dataset.freeShippingMinQty || 0);
  }

  #bindEvents() {
    const { signal } = this.#abortController;

    // Event delegation for +/- buttons inside this block.
    this.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const button = target.closest('[data-bundle-pack-action]');
        if (button instanceof HTMLButtonElement) {
          event.preventDefault();
          this.#handleQtyAction(button);
        }
      },
      { signal }
    );

    // Listen for <bundle-subscribe> mode changes so we can:
    //  - cumulate the subscription discount on top of the tier discount
    //  - inject `selling_plan` + `properties[frequency]` on each cart item
    document.addEventListener(
      'bundleSubscribe:change',
      /** @param {Event} event */ (event) => {
        const detail = /** @type {CustomEvent} */ (event).detail || {};
        this.#subscribeMode = detail.mode === 'subscribe' ? 'subscribe' : 'one-time';
        this.#subscribeFrequency = detail.frequency ?? null;
        this.#subscribeDiscountPct = Number(detail.discountPercentage) || 0;
        // Re-render so meter / events broadcast the new state.
        this.#updateUI();
      },
      { signal }
    );

    // The submit button now lives in <bundle-add-to-cart>, an independent block.
    // It dispatches `bundlePack:requestSubmit` on click; we answer with the POST.
    document.addEventListener(
      'bundlePack:requestSubmit',
      () => this.#handleSubmit(),
      { signal }
    );
  }

  /**
   * Returns the per-card max quantity. Infinity when the variant has no
   * tracked, deny-policy inventory cap (so it doesn't constrain anything).
   * @param {HTMLElement} card
   * @returns {number}
   */
  #cardMaxQty(card) {
    if (card.dataset.inventoryManagement !== 'shopify') return Infinity;
    if (card.dataset.inventoryPolicy !== 'deny') return Infinity;
    const qty = Number(card.dataset.inventoryQuantity);
    if (!Number.isFinite(qty)) return Infinity;
    return Math.max(0, qty);
  }

  /** @param {HTMLButtonElement} button */
  #handleQtyAction(button) {
    const card = button.closest('[data-bundle-pack-card]');
    if (!(card instanceof HTMLElement)) return;

    const variantId = card.dataset.variantId;
    if (!variantId) return;

    const action = button.dataset.bundlePackAction;
    const current = this.#selected.get(variantId)?.quantity ?? 0;
    const max = this.#cardMaxQty(card);
    const raw = action === 'increase' ? current + 1 : current - 1;
    const next = Math.max(0, Math.min(max, raw));

    if (next === current) return;

    if (next === 0) {
      this.#selected.delete(variantId);
      card.classList.remove('bundle-pack-card--selected');
    } else {
      const price = Number(card.dataset.price || 0);
      const comparePrice = Number(card.dataset.comparePrice || price);
      const title = card.dataset.title || '';
      const available = card.dataset.available !== 'false';
      this.#selected.set(variantId, { quantity: next, price, comparePrice, title, available });
      card.classList.add('bundle-pack-card--selected');
    }

    const input = card.querySelector('[data-bundle-pack-qty-input]');
    if (input instanceof HTMLInputElement) {
      input.value = String(next);
    }

    this.#updateUI();
  }

  #updatePlusButtons() {
    const cards = this.querySelectorAll('[data-bundle-pack-card]');
    cards.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      const plus = card.querySelector('[data-bundle-pack-action="increase"]');
      if (!(plus instanceof HTMLButtonElement)) return;
      const variantId = card.dataset.variantId || '';
      const available = card.dataset.available !== 'false';
      const current = this.#selected.get(variantId)?.quantity ?? 0;
      const max = this.#cardMaxQty(card);
      plus.disabled = !available || current >= max;
    });
  }

  #totalQuantity() {
    let total = 0;
    for (const item of this.#selected.values()) total += item.quantity;
    return total;
  }

  /** @returns {{ index: number, tier: { qty: number, discountType: string, discount: number } | null }} */
  #activeTier() {
    const total = this.#totalQuantity();
    let index = -1;
    for (let i = 0; i < this.#tiers.length; i++) {
      const t = this.#tiers[i];
      if (t && total >= t.qty) index = i;
    }
    return { index, tier: index >= 0 ? this.#tiers[index] ?? null : null };
  }

  #computeTotalPrice() {
    let subtotal = 0;
    for (const item of this.#selected.values()) {
      subtotal += item.price * item.quantity;
    }

    // Step 1 — apply the active tier discount (palier).
    let tierTotal = subtotal;
    const { tier } = this.#activeTier();
    if (tier && tier.discountType !== 'none' && tier.discount) {
      if (tier.discountType === 'percentage') {
        tierTotal = subtotal * (1 - tier.discount / 100);
      } else if (tier.discountType === 'fixed') {
        // discount stored in major units in the schema → convert to cents to match price
        tierTotal = Math.max(0, subtotal - tier.discount * 100);
      }
    }
    tierTotal = Math.round(tierTotal);

    // Step 2 — if subscribe mode is on, cumulate the subscription discount on top.
    let total = tierTotal;
    if (this.#subscribeMode === 'subscribe' && this.#subscribeDiscountPct > 0) {
      total = Math.round(tierTotal * (1 - this.#subscribeDiscountPct / 100));
    }

    return {
      subtotal,
      tierTotal,
      total,
      discounted: total < subtotal,
    };
  }

  #updateUI() {
    const total = this.#totalQuantity();
    const { index: activeIndex } = this.#activeTier();

    this.#updatePlusButtons();

    // Filled circles + inner dots, subdued prices.
    const circles = this.querySelectorAll('[data-bundle-pack-circle]');
    circles.forEach((circle, i) => {
      const reached = i <= activeIndex;
      circle.classList.toggle('filled', reached);
      const inner = circle.querySelector('.quantity-meter-addon-item__circle-inner');
      if (inner) inner.classList.toggle('filled', reached);
    });
    const prices = this.querySelectorAll('[data-bundle-pack-meter-price]');
    prices.forEach((price, i) => {
      price.classList.toggle('subdued', i > activeIndex);
    });

    // Progress line — pixel-precise: align both lines (background + filled)
    // on the centre of the first/last/active circles.
    this.#updateProgressLine(activeIndex);

    // Compute prices once for both the badge state, the submit button and the
    // event broadcast.
    const priceState = this.#computeTotalPrice();

    // Shipping badge: always visible. Locked (grey + padlock) until the cart
    // total (after discount) reaches the free-shipping threshold, then unlocked
    // (red + tag).
    if (this.refs.meterBadge instanceof HTMLElement) {
      const isUnlocked =
        this.#freeShippingMode === 'quantity'
          ? this.#freeShippingMinQty > 0 && total >= this.#freeShippingMinQty
          : this.#freeShippingThreshold > 0 && priceState.total >= this.#freeShippingThreshold;
      this.refs.meterBadge.classList.toggle('is-unlocked', isUnlocked);
      this.refs.meterBadge.classList.toggle('is-locked', !isUnlocked);
    }

    // Broadcast the current totals so <bundle-subscribe>, <bundle-add-to-cart>
    // and any future sibling block can refresh their own UI. We send the
    // tier-discounted price as `discountedTotal` — the subscribe block adds
    // its own % discount on top, and the add-to-cart block uses it for the
    // submit-button price.
    document.dispatchEvent(
      new CustomEvent('bundlePack:totalChanged', {
        detail: {
          subtotal: priceState.subtotal,
          discountedTotal: priceState.tierTotal,
          finalTotal: priceState.total,
          quantity: total,
          tierQtys: this.#tiers.map((t) => t.qty),
        },
      })
    );
  }

  /** @param {number} activeIndex */
  #updateProgressLine(activeIndex) {
    const items = Array.from(this.querySelectorAll('[data-bundle-pack-meter-item]'));
    const firstItem = items[0];
    const lastItem = items[items.length - 1];
    if (!firstItem || !lastItem) return;

    const firstCircle = firstItem.querySelector('.quantity-meter-addon-item__circle');
    const lastCircle = lastItem.querySelector('.quantity-meter-addon-item__circle');
    if (!(firstCircle instanceof HTMLElement) || !(lastCircle instanceof HTMLElement)) return;

    // CORRECTION: the items row is offset 10px from the meter's padded edge,
    // so add 10 to align the lines on the circle centres.
    const CORRECTION = 10;
    const firstCenter = firstCircle.offsetLeft + firstCircle.offsetWidth / 2 + CORRECTION;
    const lastCenter = lastCircle.offsetLeft + lastCircle.offsetWidth / 2 + CORRECTION;

    const bgLine = this.querySelector('.quantity-meter-addon-line:not(.filled)');
    if (bgLine instanceof HTMLElement) {
      bgLine.style.left = firstCenter + 'px';
      bgLine.style.width = lastCenter - firstCenter + 'px';
    }

    const progressLine = this.refs.progressLine;
    if (!(progressLine instanceof HTMLElement)) return;

    progressLine.style.left = firstCenter + 'px';
    if (activeIndex < 0) {
      progressLine.style.width = '0';
      return;
    }

    const activeItem = items[activeIndex];
    const activeCircle = activeItem?.querySelector('.quantity-meter-addon-item__circle');
    if (activeCircle instanceof HTMLElement) {
      const activeCenter = activeCircle.offsetLeft + activeCircle.offsetWidth / 2 + CORRECTION;
      // Reach at least the centre of the first circle as soon as tier 1 is hit,
      // then extend up to the active tier's circle.
      progressLine.style.width = Math.max(0, activeCenter - firstCenter) + 'px';
    }
  }

  async #handleSubmit() {
    const total = this.#totalQuantity();
    if (total < this.#minQty) return;

    const isSubscribe = this.#subscribeMode === 'subscribe';
    const items = [...this.#selected.entries()].map(([variantId, value]) => {
      /** @type {{ id: number, quantity: number, selling_plan?: number, properties?: Record<string, string> }} */
      const item = {
        id: Number(variantId),
        quantity: value.quantity,
      };
      if (isSubscribe) {
        // Resolve the selling plan from the card's data attribute (filled by
        // Liquid from product.selling_plan_groups). Falls back silently to a
        // one-time line item if no plan is configured for that product.
        const card = this.querySelector(`[data-bundle-pack-card][data-variant-id="${variantId}"]`);
        const sellingPlan = card instanceof HTMLElement ? card.dataset.sellingPlanId : '';
        if (sellingPlan) {
          item.selling_plan = Number(sellingPlan);
          if (this.#subscribeFrequency) {
            item.properties = { frequency: this.#subscribeFrequency };
          }
        }
      }
      return item;
    });

    if (items.length === 0) return;

    /** @type {string} */
    const cartAddUrl =
      // @ts-ignore — Theme is a global injected by snippets/scripts.liquid
      (typeof Theme !== 'undefined' && Theme.routes?.cart_add_url) || '/cart/add.js';

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });
      const payload = await response.json();

      if (payload.status) {
        this.dispatchEvent(
          new CartErrorEvent(this.id, payload.message, payload.description, payload.errors)
        );
        // Tell <bundle-add-to-cart> to surface the error and re-enable its button.
        document.dispatchEvent(
          new CustomEvent('bundlePack:submitError', {
            detail: { message: payload.message || payload.description || '' },
          })
        );
        return;
      }

      const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
      // Bubble: true → cart-drawer + cart count listener (`cart:update`) react automatically.
      this.dispatchEvent(
        new CartAddEvent(undefined, this.id, {
          source: 'bundle-pack',
          itemCount,
        })
      );

      // Tinker's <cart-drawer-component> doesn't auto-open on cart:update — call its
      // public open() method directly so the drawer slides in.
      const cartDrawer = /** @type {HTMLElement & { open?: () => void } | null} */ (
        document.querySelector('cart-drawer-component')
      );
      cartDrawer?.open?.();

      // Tell <bundle-add-to-cart> to clear its in-flight state.
      document.dispatchEvent(new CustomEvent('bundlePack:submitDone', { detail: { itemCount } }));

      // Reset selection so the customer can compose a new pack.
      this.#reset();
    } catch (error) {
      console.error('[bundle-pack] add to cart failed', error);
      document.dispatchEvent(
        new CustomEvent('bundlePack:submitError', {
          detail: { message: this.dataset.genericErrorText || 'Une erreur est survenue.' },
        })
      );
    }
  }

  #reset() {
    this.#selected.clear();
    const cards = this.querySelectorAll('[data-bundle-pack-card]');
    cards.forEach((card) => {
      card.classList.remove('bundle-pack-card--selected');
      const input = card.querySelector('[data-bundle-pack-qty-input]');
      if (input instanceof HTMLInputElement) input.value = '0';
    });
    this.#updateUI();
  }
}

if (!customElements.get('bundle-pack')) {
  customElements.define('bundle-pack', BundlePack);
}
